import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2,
  XCircle,
  Instagram,
  Calendar,
  Sparkles,
  ShieldCheck,
  AlertCircle,
  MessageSquare,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API = `${BACKEND_URL}/api`;

export default function ClientReviewPage() {
  const { token } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [decisionDone, setDecisionDone] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    axios
      .get(`${API}/review/post/${token}`)
      .then((res) => {
        setPost(res.data);
        if (res.data.status === "approved" || res.data.status === "declined") {
          setDecisionDone(res.data.status);
        }
        if (res.data.client_feedback) {
          setFeedback(res.data.client_feedback);
        }
      })
      .catch((err) => {
        setError(err.response?.data?.detail || "Invalid or expired review link.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleAction = async (action) => {
    if (action === "decline" && !feedback.trim()) {
      toast.error("Please provide brief feedback so the team can revise this post.");
      return;
    }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/review/post/${token}/action`, {
        action,
        feedback,
      });
      setDecisionDone(res.data.status);
      setPost((prev) => ({ ...prev, status: res.data.status, client_feedback: feedback }));
      toast.success(
        action === "approve"
          ? "Post approved! It will be scheduled for auto-publishing."
          : "Feedback submitted. The creative team has been notified."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit response");
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground text-sm">Loading Instagram preview…</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full p-6 text-center space-y-4">
          <AlertCircle className="w-12 h-12 text-rose-500 mx-auto" />
          <h2 className="text-xl font-bold">Review Link Not Found</h2>
          <p className="text-muted-foreground text-sm">{error || "This link may have expired or been removed."}</p>
        </Card>
      </div>
    );
  }

  const currentStatus = post.status;
  const isApproved = currentStatus === "approved";
  const isDeclined = currentStatus === "declined";

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header Branding */}
        <div className="flex items-center justify-between border-b pb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-yellow-500 via-pink-500 to-purple-600 flex items-center justify-center text-white shadow-sm">
              <Instagram className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-bold text-lg leading-tight">Instagram Content Approval</h1>
              <p className="text-xs text-muted-foreground">Prepared for {post.client_name}</p>
            </div>
          </div>
          <Badge
            className={
              isApproved
                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                : isDeclined
                ? "bg-rose-100 text-rose-800 border-rose-300"
                : "bg-amber-100 text-amber-800 border-amber-300"
            }
          >
            {isApproved ? "Approved by Client" : isDeclined ? "Revisions Requested" : "Awaiting Your Review"}
          </Badge>
        </div>

        {/* Instagram Post Mockup Card */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white rounded-2xl border shadow-sm overflow-hidden">
          {/* Media preview */}
          <div className="bg-slate-900 flex items-center justify-center min-h-[350px] relative">
            {post.media_url ? (
              <img
                src={post.media_url}
                alt="Instagram Creative preview"
                className="max-h-[460px] w-full object-contain"
              />
            ) : (
              <div className="text-center p-8 text-slate-400 space-y-2">
                <Instagram className="w-12 h-12 mx-auto opacity-50" />
                <p className="text-xs">No media preview available</p>
              </div>
            )}
            <div className="absolute top-3 right-3 bg-black/60 text-white text-[10px] px-2 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-yellow-400" /> Instagram Feed / Reel
            </div>
          </div>

          {/* Post Copy and Details */}
          <div className="p-6 flex flex-col justify-between space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-muted-foreground pb-2 border-b">
                <span>Account: @{post.client_name.toLowerCase().replace(/\s+/g, "")}</span>
                {post.scheduled_time && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Target: {new Date(post.scheduled_time).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                  </span>
                )}
              </div>

              <div>
                <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                  Post Caption
                </Label>
                <p className="mt-1.5 text-sm whitespace-pre-line leading-relaxed text-slate-800">
                  {post.caption}
                </p>
              </div>

              {post.hashtags?.length > 0 && (
                <div>
                  <Label className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                    Hashtags
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {post.hashtags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md font-mono"
                      >
                        #{tag.replace(/^#/, "")}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Current Decision Status Banner */}
            {isApproved && (
              <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 space-y-1">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  Post Approved for Publishing
                </div>
                <p className="text-xs text-emerald-700">
                  This post will automatically publish to Instagram at its scheduled time.
                </p>
                {post.client_feedback && (
                  <p className="text-xs italic pt-1">Note: "{post.client_feedback}"</p>
                )}
              </div>
            )}

            {isDeclined && (
              <div className="p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 space-y-1">
                <div className="flex items-center gap-2 font-semibold text-sm">
                  <XCircle className="w-4 h-4 text-rose-600" />
                  Changes Requested
                </div>
                <p className="text-xs text-rose-700">
                  {post.client_feedback
                    ? `Feedback recorded: "${post.client_feedback}"`
                    : "No specific notes provided yet. You can submit revision feedback below."}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Action & Feedback Box: Always accessible so client can provide or update notes */}
        <Card className="p-6 border border-border shadow-sm bg-white">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-primary" />
            {isApproved ? "Approved Decision Details" : isDeclined ? "Submit or Update Revision Feedback" : "Client Decision & Feedback"}
          </h3>
          <p className="text-xs text-muted-foreground mb-4">
            Type your revision notes, feedback, or copy adjustments for the creative team.
          </p>

          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1.5 mb-1.5">
                <MessageSquare className="w-3.5 h-3.5 text-primary" />
                Your Feedback / Change Requests
              </Label>
              <textarea
                className="w-full p-3 rounded-lg border border-input text-sm outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary min-h-[90px] bg-background"
                placeholder="e.g. Please change the caption headline, swap the cover image to the second slide, or adjust the hashtags..."
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-1">
              <Button
                type="button"
                onClick={() => handleAction("approve")}
                disabled={actionLoading}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-medium"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {actionLoading ? "Submitting…" : isApproved ? "Update Approval Note" : "Approve & Schedule Post"}
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={() => handleAction("decline")}
                disabled={actionLoading}
                className="flex-1 border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
              >
                <XCircle className="w-4 h-4 mr-2" />
                {actionLoading ? "Submitting…" : isDeclined ? "Update Revision Notes" : "Request Changes (Decline)"}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
